import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  useTheme,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  MenuBook as MenuBookIcon,
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

function getScoreColor(score) {
  if (score >= 93.01) return '#4caf50';
  if (score >= 80) return '#ff9800';
  return '#f44336';
}

function getScoreBackground(score) {
  if (score >= 93.01) return '#e8f5e8';
  if (score >= 80) return '#fff3e0';
  return '#ffebee';
}

function formatReadingTime(seconds) {
  if (!seconds || seconds === 0) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
}

function getPassageImage(passage) {
  const backendUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '').replace(/\/$/, '');
  if (passage && passage.image) {
    return `${backendUrl}/passages/${passage.image}`;
  }
  return '/logo192.png';
}

export default function SequenceReport() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [sequence, setSequence] = useState(null);
  const [reports, setReports] = useState([]); // one per passage (in order)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        // 1) Load sequence with passages and completed tests
        const seqRes = await api.get(`/api/sequences/${linkId}`);
        const seq = seqRes.data;
        setSequence(seq);

        // 2) For each passage index, if a completed test exists, fetch its report
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Map completedPassages by index based on linkId suffix
        const indexToTest = {};
        (seq.completedPassages || []).forEach(t => {
          const match = String(t.linkId || '').match(/-passage-(\d+)$/);
          if (match) indexToTest[parseInt(match[1], 10)] = t;
        });

        const gradingMethod = seq.gradingMethod || 'whisper';
        const endpointFor = (testLink) => gradingMethod === 'whisper' ? `${process.env.REACT_APP_API_URL}/api/tests/${testLink}/whisper-result` : `${process.env.REACT_APP_API_URL}/api/tests/${testLink}/result`;

        const fetchPromises = (seq.passages || [])
          .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
          .map((p, idx) => {
            const test = indexToTest[idx];
            if (!test) return Promise.resolve({ passage: p.passageId, status: 'not_attempted', report: null, index: idx });
            return api.get(endpointFor(test.linkId))
              .then(r => ({ passage: p.passageId, status: 'completed', report: r.data, index: idx, testLinkId: test.linkId }))
              .catch(() => ({ passage: p.passageId, status: 'attempted', report: null, index: idx, testLinkId: test.linkId }));
          });

        const results = await Promise.all(fetchPromises);
        setReports(results);
        setLoading(false);
      } catch (e) {
        console.error('Failed to load sequence report:', e);
        setError('Failed to load sequence report');
        setLoading(false);
      }
    }
    fetchData();
  }, [linkId]);

  const aggregate = useMemo(() => {
    if (!sequence || reports.length === 0) return null;
    let totalWords = 0;
    let correctWords = 0;
    let omittedWords = 0;
    let totalReadingTime = 0;
    let completedCount = 0;
    reports.forEach(item => {
      if (item.status !== 'completed' || !item.report) return;
      completedCount += 1;
      const r = item.report;
      if (typeof r.readingTime === 'number') totalReadingTime += r.readingTime;
      if (sequence.gradingMethod === 'whisper') {
        const details = r.reportDetails || {};
        totalWords += details.totalWords || 0;
        correctWords += details.correctWords || 0;
        omittedWords += details.omittedWords || 0;
      } else {
        const details = r.reportDetails || {};
        totalWords += details.debug_totalWordsAttempted || 0;
        correctWords += details.debug_totalWordsCorrect || 0;
      }
    });
    const overallAccuracy = totalWords > 0 ? Math.round((correctWords / totalWords) * 100) : 0;
    const wpm = totalReadingTime > 0 ? Math.round((correctWords / totalReadingTime) * 60) : null;
    return {
      totalPassages: (sequence.passages || []).length,
      completedCount,
      overallAccuracy,
      totalReadingTime,
      wpm,
      correctWords,
      totalWords,
      omittedWords
    };
  }, [sequence, reports]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!sequence) return null;

  const gradingLabel = sequence.gradingMethod === 'whisper' ? 'Whisper AI' : 'SpeechAce';
  const ordered = (sequence.passages || []).slice().sort((a, b) => a.sequenceOrder - b.sequenceOrder);

  return (
    <Box p={3} minHeight="100vh" sx={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)', fontFamily: 'Nunito, sans-serif' }}>
      <Container maxWidth="lg">
        {/* Header with Educational Theme */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip title="Back to Results">
              <IconButton 
                onClick={() => navigate('/dashboard', { state: { defaultTab: 1 } })}
                sx={{ 
                  backgroundColor: '#f5f5f5', 
                  '&:hover': { backgroundColor: '#e0e0e0' },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#4fc3f7', mb: 1 }}>
                📊 Reading Sequence Report
              </Typography>
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Student: {sequence.childName}
              </Typography>
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Empowering young readers, one story at a time! • {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={`✨ Graded by ${gradingLabel}`} 
            sx={{ 
              fontWeight: 700, 
              fontSize: 16, 
              px: 3, 
              py: 1.5, 
              borderRadius: 3,
              background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
              color: 'white',
              boxShadow: '0 2px 8px rgba(79,195,247,0.20)'
            }} 
          />
        </Box>

        {/* Elegant Analytics Cards */}
        {aggregate && (
          <Box mb={4}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper elevation={2} sx={{ 
                  p: 2.5, 
                  borderRadius: 3, 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  background: getScoreBackground(aggregate.overallAccuracy),
                  border: `1px solid ${getScoreColor(aggregate.overallAccuracy)}`,
                  boxShadow: `0 2px 8px ${getScoreColor(aggregate.overallAccuracy)}15`,
                  minHeight: 80
                }}>
                  <Avatar sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: getScoreColor(aggregate.overallAccuracy)
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Accuracy</Typography>
                    <Typography variant="h4" fontWeight={800} color={getScoreColor(aggregate.overallAccuracy)} lineHeight={1}>
                      {aggregate.overallAccuracy}%
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper elevation={2} sx={{ 
                  p: 2.5, 
                  borderRadius: 3, 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  background: '#fff3e0',
                  border: '1px solid #ff9800',
                  boxShadow: '0 2px 8px rgba(255,152,0,0.15)',
                  minHeight: 80
                }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#ff9800' }}>
                    <SpeedIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>WPM</Typography>
                    <Typography variant="h4" fontWeight={800} color="#ff9800" lineHeight={1}>
                      {aggregate.wpm ?? 'N/A'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper elevation={2} sx={{ 
                  p: 2.5, 
                  borderRadius: 3, 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  background: '#e8f5e8',
                  border: '1px solid #81c784',
                  boxShadow: '0 2px 8px rgba(129,199,132,0.15)',
                  minHeight: 80
                }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#81c784' }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Completed</Typography>
                    <Typography variant="h4" fontWeight={800} color="#81c784" lineHeight={1}>
                      {aggregate.completedCount}/{aggregate.totalPassages}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper elevation={2} sx={{ 
                  p: 2.5, 
                  borderRadius: 3, 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  background: '#e3f2fd',
                  border: '1px solid #4fc3f7',
                  boxShadow: '0 2px 8px rgba(79,195,247,0.15)',
                  minHeight: 80
                }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#4fc3f7' }}>
                    <TimerIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Time</Typography>
                    <Typography variant="h4" fontWeight={800} color="#4fc3f7" lineHeight={1}>
                      {formatReadingTime(aggregate.totalReadingTime)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Educational Passage Cards */}
        <Paper elevation={3} sx={{ borderRadius: 3, p: 3, background: '#f9fafc' }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Avatar sx={{ bgcolor: '#4fc3f7', width: 48, height: 48 }}>
              <MenuBookIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#4fc3f7">
                📚 Reading Passages Portfolio
              </Typography>
              <Typography color="text.secondary">
                Detailed performance analysis for each story
              </Typography>
            </Box>
          </Box>
          
          <Grid container spacing={3}>
            {ordered.map((p, idx) => {
              const r = reports.find(x => x.index === idx) || { status: 'not_attempted' };
              const isCompleted = r.status === 'completed' && r.report;
              const accuracy = isCompleted ? r.report.accuracy : null;
              const readingTime = isCompleted ? r.report.readingTime : null;
              const wpm = isCompleted ? (r.report.wordsPerMinute ?? null) : null;
              let statusLabel = 'Not Started';
              let statusColor = 'default';
              let cardBg = '#ffffff';
              let borderColor = '#e0e0e0';
              
              if (r.status === 'completed') { 
                statusLabel = '✅ Completed'; 
                statusColor = 'success'; 
                cardBg = '#e8f5e8';
                borderColor = '#81c784';
              }
              else if (r.status === 'attempted') { 
                statusLabel = '⏳ Processing'; 
                statusColor = 'info';
                cardBg = '#e3f2fd';
                borderColor = '#4fc3f7';
              }
              else if (idx < sequence.currentPassageIndex) { 
                statusLabel = '📖 In Progress'; 
                statusColor = 'warning';
                cardBg = '#fff3e0';
                borderColor = '#ff9800';
              }
              
              return (
                <Grid item xs={12} md={6} key={idx}>
                  <Card sx={{
                    borderRadius: 3,
                    background: cardBg,
                    border: `2px solid ${borderColor}`,
                    boxShadow: `0 4px 16px ${borderColor}20`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${borderColor}30`
                    }
                  }}>
                    <Box sx={{ display: 'flex', height: 200 }}>
                      {/* Image Section */}
                      <Box sx={{ width: 140, position: 'relative' }}>
                        <CardMedia
                          component="img"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}
                          image={getPassageImage(p.passageId)}
                          alt={p.passageId?.title}
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          background: 'linear-gradient(135deg, #4fc3f7 0%, #81c784 100%)',
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5
                        }}>
                          <Typography variant="h6" fontWeight={800} color="white">
                            #{idx + 1}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Content Section */}
                      <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, lineHeight: 1.2 }}>
                          {p.passageId?.title}
                        </Typography>
                        
                        <Chip 
                          label={statusLabel} 
                          color={statusColor} 
                          size="small" 
                          sx={{ alignSelf: 'flex-start', mb: 2, fontWeight: 700 }}
                        />
                        
                        {isCompleted ? (
                          <Box sx={{ mt: 'auto' }}>
                            <Grid container spacing={1} sx={{ mb: 2 }}>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">Accuracy</Typography>
                                <Typography variant="h6" fontWeight={700} color={getScoreColor(accuracy)}>
                                  {accuracy}%
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">WPM</Typography>
                                <Typography variant="h6" fontWeight={700} color="#ff9800">
                                  {wpm ?? 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">Time</Typography>
                                <Typography variant="h6" fontWeight={700} color="#4fc3f7">
                                  {formatReadingTime(readingTime)}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Button
                              variant="contained"
                              fullWidth
                              size="small"
                              sx={{ 
                                borderRadius: 2, 
                                fontWeight: 700,
                                background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                                boxShadow: '0 2px 8px rgba(79,195,247,0.20)'
                              }}
                              startIcon={<AssignmentIcon />}
                              onClick={() => navigate(`/test/${r.testLinkId}/report`)}
                            >
                              View Full Report
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 'auto', textAlign: 'center', py: 2 }}>
                            <SchoolIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              {r.status === 'attempted' ? 'Processing results...' : 'Waiting to start'}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        {/* Educational Footer */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ 
            color: '#4fc3f7', 
            fontWeight: 700,
            fontStyle: 'italic',
            mb: 1
          }}>
            🎓 Empowering young readers, one story at a time!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Report generated with advanced AI analysis • Making learning fun and effective
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}